import {Injectable, signal} from '@angular/core';
import {Session} from '../models/session.model';
import {Links} from '../models/HAL/links.model';
import {Team} from '../models/HAL/team-hal.model';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly _session = signal<Session>({})
  public session = this._session.asReadonly();

  private readonly SESSION_KEY_LOCAL_STORAGE = "session";

  constructor() {
    let restoredSession = this.restoreSessionFromLocalStorage();
    if (restoredSession) {
      this._session.set({...restoredSession});
    }
  }

  public setSession(session: Session) {
    localStorage.setItem(this.SESSION_KEY_LOCAL_STORAGE, JSON.stringify(session));
    this._session.set({...session});
  }

  public getSession(): Session {
    return this._session();
  }

  public revokeSession() {
    localStorage.removeItem(this.SESSION_KEY_LOCAL_STORAGE);
    this._session.set({} as Session);
  }

  public unselectTeam() {
    let session = this.getSession();
    session.currentTeam = undefined;
    this.setSession({...session});
  }

  public incrementUnassignedCaseCount(delta: number) {
    const session = this.getSession();
    if (!session.currentTeam || delta === 0) {
      return;
    }
    const updatedCount = Math.max(0, (session.currentTeam.unassignedCaseCount ?? 0) + delta);
    session.currentTeam.unassignedCaseCount = updatedCount;
    if (session.teams) {
      session.teams = session.teams.map(team =>
        team.identification === session.currentTeam?.identification
          ? {...team, unassignedCaseCount: updatedCount}
          : team
      );
    }
    this.setSession({...session});
  }

  /**
   * The restoring of the session from the localStorage poses some difficulties regarding the types. Just reading it via
   * JSON.parse(...) returns a JavaScript object, but no types (for examples the Links). This object has helper functions.
   * By simply parsing it, the methods on Links are unknown. Therefore, we need to explicitly read those parts as Links.
   */
  private restoreSessionFromLocalStorage() {
    const sessionJson = localStorage.getItem(this.SESSION_KEY_LOCAL_STORAGE);
    if (sessionJson) {
      let restoredSession = JSON.parse(sessionJson) as Session;
      if (restoredSession.currentTeam?._links) {
        restoredSession.currentTeam._links = Links.fromJSON(restoredSession.currentTeam._links);
      }
      if (restoredSession.user?._links) {
        restoredSession.user._links = Links.fromJSON(restoredSession.user?._links);
      }
      if (restoredSession.teams) {
        let restoredTeams: Team[] = []
        restoredSession.teams.forEach(team => {
          let restoredTeam = team;
          restoredTeam._links = Links.fromJSON(team._links);
          restoredTeams.push(restoredTeam);
        });
        restoredSession.teams = restoredTeams;
      }
      return restoredSession;
    }
    return undefined;
  }

}
